import axios from "../API/axios";
import { getLangKey } from "../utils/language";

const sessionId = () => localStorage.getItem("sessionId") || "";

const POLL_CACHE_MS = 5 * 60 * 1000;
const NO_POLL_CACHE_MS = 15 * 60 * 1000;

/** In-memory poll cache: postId -> { data, ts } */
const pollCache = new Map();
/** In-flight dedupe: postId -> Promise */
const pollInflight = new Map();

function normalizePollResponse(data) {
  if (!data) return null;
  const options = data.options ?? data.Options ?? [];
  const pollId = data.pollId ?? data.PollId;
  if (!pollId || !Array.isArray(options) || options.length === 0) return null;
  return data;
}

export function setPollCacheForPost(postId, data) {
  const key = String(postId);
  pollCache.set(key, { data: normalizePollResponse(data), ts: Date.now() });
}

export function invalidatePollCache(postId) {
  pollCache.delete(String(postId));
}

/** Fire-and-forget impression batch (never throws to caller UX). */
export async function recordImpressions(items) {
  try {
    if (!Array.isArray(items) || items.length === 0) return;
    await axios.post("/Impression/Batch", {
      DeviceType: "Web",
      sessionId: sessionId(),
      LangKey: getLangKey(),
      Items: items.map((i) => ({
        TargetType: i.targetType,
        TargetId: String(i.targetId),
        Source: i.source ?? 1,
      })),
    });
  } catch {
    /* analytics must not break UX */
  }
}

export async function fetchPollByPost(postId) {
  const key = String(postId);
  if (!key) return null;

  const cached = pollCache.get(key);
  if (cached) {
    const ttl = cached.data ? POLL_CACHE_MS : NO_POLL_CACHE_MS;
    if (Date.now() - cached.ts < ttl) return cached.data;
  }

  if (pollInflight.has(key)) return pollInflight.get(key);

  const request = (async () => {
    try {
      const res = await axios.get(
        `/Poll/GetByPost?deviceType=Web&PostId=${postId}&langKey=${encodeURIComponent(getLangKey())}`,
      );
      const data = normalizePollResponse(
        res?.data?.responseData ?? res?.data?.ResponseData ?? null,
      );
      pollCache.set(key, { data, ts: Date.now() });
      return data;
    } catch {
      pollCache.set(key, { data: null, ts: Date.now() });
      return null;
    } finally {
      pollInflight.delete(key);
    }
  })();

  pollInflight.set(key, request);
  return request;
}

export async function createPollForPost(postId, { options, isMultipleChoice = false, description = "" }) {
  const parsedPostId = Number(postId);
  if (!parsedPostId || !Array.isArray(options) || options.length < 2) return null;

  const res = await axios.post("/Poll/CreateWithPost", {
    deviceType: "Web",
    langKey: getLangKey(),
    sessionId: sessionId(),
    postId: parsedPostId,
    description: description,
    isMultipleChoice: isMultipleChoice,
    isAnonymous: true,
    isCommentsEnabled: true,
    options: options,
  });
  invalidatePollCache(parsedPostId);
  return res?.data?.responseData ?? res?.data?.ResponseData ?? null;
}

export async function votePoll(pollId, optionIds, postId) {
  const res = await axios.post("/Poll/Vote", {
    deviceType: "Web",
    langKey: getLangKey(),
    sessionId: sessionId(),
    pollId: pollId,
    optionIds: optionIds,
  });
  const data = res?.data?.responseData ?? res?.data?.ResponseData ?? null;
  if (data && postId != null) {
    setPollCacheForPost(postId, data);
  }
  return data;
}

/** Remove the current user's vote (double-click on voted option). */
export async function unvotePoll(pollId, postId) {
  const body = {
    deviceType: "Web",
    langKey: getLangKey(),
    sessionId: sessionId(),
    pollId: pollId,
    optionIds: [],
  };

  try {
    const res = await axios.post("/Poll/Vote", body);
    const data = res?.data?.responseData ?? res?.data?.ResponseData ?? null;
    if (postId != null) {
      invalidatePollCache(postId);
      if (data) {
        setPollCacheForPost(postId, data);
        return data;
      }
      return await fetchPollByPost(postId);
    }
    return data;
  } catch {
    if (postId == null) return null;
    invalidatePollCache(postId);
    return await fetchPollByPost(postId);
  }
}

export async function fetchImpressionSummary(targetType, targetId) {
  const res = await axios.get(
    `/Impression/Summary?deviceType=Web&TargetType=${targetType}&TargetId=${encodeURIComponent(targetId)}&LangKey=${encodeURIComponent(getLangKey())}`,
  );
  return res?.data?.responseData ?? res?.data?.ResponseData ?? null;
}
